import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { virtualize } from '@adornis/chemistry/directives/when-seen.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import '@adornis/chemistry/elements/components/x-link.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { SubscriptionPagination } from '@adornis/pagination/paginatable.js';
import { Product } from '@chancenwerk/shared/_cms/products/entities/Product.js';
import type { ProductLKK } from '@chancenwerk/shared/_cms/products/entities/ProductLKK.js';
import '@chancenwerk/client/components/chancen-component-zip-city-picker.js';
import '@chancenwerk/client/components/semantics/chancen-component-semantic-product-picker.js';
import { Adresse } from '@chancenwerk/shared/adresse.js';
import { KpfFilter } from '@chancenwerk/shared/filter/KpfFilter.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import '@chancenwerk/client/design/components/cr-button.js';
import '@chancenwerk/client/design/components/cr-card.js';
import '@chancenwerk/client/design/components/cr-dropdown-selection.js';
import '@chancenwerk/client/design/components/cr-flex.js';
import '@chancenwerk/client/design/components/cr-grid.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import '@chancenwerk/client/design/components/cr-progress-bar.js';
import '@chancenwerk/client/design/components/fonts.js';
import { KPFAPI } from '@chancenwerk/shared/master-data-management/kpf/api/KPFAPI.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { takeUntil } from 'rxjs';
import '../components/board-component-course-digital-card.js';
import '../components/board-component-kpf-filter.js';
import '../components/board-component-overview-card.js';
import { globalKpfFilter } from '../globalSubjects.js';

const kpfSelector: BaseQLSelectionSet<KPF | KPF<ProductLKK>> = {
  _id: 1,
  _class: 1,
  produkt: { ...Product.allFields, useV2Courses: 1 },
  schoolID: 1,
  school: { kuerzel: 1, name: 1, address: Adresse.allFields },
  schuKos: { _id: 1 },
  leKos: { _id: 1 },
  usStudents: { _id: 1 },
  osStudents: { _id: 1 },
};

/**
 * @customElement board-page-overview
 */
@customElement('board-page-overview')
export class BoardPageOverview extends CrPage {
  @state() private readonly _filter = globalKpfFilter;

  private readonly _kpfsPaginated = new SubscriptionPagination<KPF, Array<KpfFilter>>(
    KPFAPI.subscribeByFilter.bind(KPF),
    [new KpfFilter({ aspects: [new BooleanAspect({ customID: 'active', value: true })] })],
    kpfSelector,
    10,
    5,
  );

  constructor() {
    super();
    this.label = 'KPF-Kursübersicht';
    this.description = 'Hier kannst du alle KPFs sehen.';
    this.permission = ChancenPermission.board.VIEW_KPF_OVERVIEW;
    this.stickyHeader = true;
    this.topScrollButton = true;
  }

  @state() private readonly _kpfs = new RXController(this, this._kpfsPaginated.data);

  override connectedCallback(): void {
    this._kpfsPaginated.loadingInitially.pipe(takeUntil(this.disconnected)).subscribe(() => {
      this.requestUpdate();
    });

    super.connectedCallback();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();

    this._kpfsPaginated.stop();
  }

  override content() {
    return html`
      <cr-flex space="sm" padding="xs">
        <board-component-kpf-filter .kpfsPaginated=${this._kpfsPaginated}></board-component-kpf-filter>
        ${this._content()}
      </cr-flex>
    `;
  }

  private _content() {
    if (this._kpfsPaginated.loadingInitially.value || !this._kpfsPaginated.complete) {
      return html` <cr-flex padding="xl" w100 h100 center crossaxis-center> <cr-loader></cr-loader> </cr-flex> `;
    }

    const filtered = this._kpfs.value.filter(kpf => kpf.produkt);
    const diff = this._kpfs.value.length - filtered.length;
    return html`
      <cr-flex flex horizontal space="md">
        <cr-flex space="md" ${css({ width: '100%' })}>
          ${repeat(
            filtered,
            kpf => kpf._id,
            (kpf, index) =>
              html` ${virtualize(
                html`
                  <board-component-overview-card
                    .showProductLabel=${!this._filter.value.productClasses?.at(0)}
                    .kpf=${kpf}
                  ></board-component-overview-card>
                `,
                oldHeight => html` <div ${css({ height: oldHeight + 'px' })}></div> `,
                {
                  dontUnload: true,
                  onRender: () => {
                    // The current rendered item is near the end of the list && we haven't already loaded the new items
                    if (
                      index === filtered.length - 5 &&
                      this._kpfsPaginated.totalRequested.getValue() < index + diff + 10
                    )
                      this._kpfsPaginated.next();
                  },
                },
              )}`,
          )}
          ${this._kpfsPaginated.loadingInitially.value || !this._kpfsPaginated.complete.value
            ? html` ${new Array(5).fill(null).map((_, i) => html` ${this._loaderCard((i / 5) * 0.5 + 's')} `)} `
            : nothing}
        </cr-flex>
      </cr-flex>
    `;
  }

  private _loaderCard(delay: string) {
    return html`
      <cr-card space="sm">
        <cr-flat-loader ${css({ animationDelay: delay })}></cr-flat-loader>
        <cr-flat-loader ${css({ height: '30px', animationDelay: delay })}></cr-flat-loader>
        <cr-flat-loader ${css({ height: '30px', animationDelay: delay })}></cr-flat-loader>
        <cr-flat-loader ${css({ height: '30px', animationDelay: delay })}></cr-flat-loader>
        <cr-flat-loader ${css({ height: '30px', animationDelay: delay })}></cr-flat-loader>
      </cr-card>
    `;
  }
}
